<template>
    <div
        v-if="locales.length > 1"
        class="flex items-center gap-x-1 relative w-fit"
    >
        <div
            v-for="locale in mappedLocales"
            :key="locale.id"
            class="flex items-center rounded-sm gap-x-2"
        >
            <button
                @click="$emit('update:modelValue', locale.id)"
                :class="[
                    'rounded-sm flex p-[1.5px] transition duration-button-hover group',
                    {
                        'ring-yellow-500 ring-[2.5px]':
                            modelValue != locale.id && locale.isMissingContent,
                    },
                    {
                        'ring-transparent ring-2':
                            modelValue != locale.id && !locale.isMissingContent,
                    },
                ]"
            >
                <div
                    :class="[
                        {
                            'opacity-30 group-hover:opacity-100 transition-opacity duration-button-hover':
                                modelValue != locale.id,
                        },
                        `fi fi-${locale.countryCode} w-8 h-6 bg-cover rounded-sm border border-gray-200`,
                    ]"
                />
            </button>
        </div>
        <div class="absolute -right-5">
            <HorizontalSlideTransition
                :show="localesWithMissingContent.length > 0"
            >
                <div class="bg-yellow-500 rounded-full p-0.5 ml-2">
                    <TextTooltip :text="missingTranslationsMessage">
                        <PhExclamationMark
                            class="size-3 text-white cursor-help"
                            weight="bold"
                        />
                    </TextTooltip>
                </div>
            </HorizontalSlideTransition>
        </div>
    </div>
</template>

<script>
import { PhExclamationMark } from '@phosphor-icons/vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'
import HorizontalSlideTransition from '@/Components/Transitions/HorizontalSlideTransition.vue'

export default {
    components: {
        PhExclamationMark,
        TextTooltip,
        HorizontalSlideTransition,
    },

    emits: ['update:modelValue'],

    props: {
        localesWithMissingContent: {
            type: Array,
            default: () => [],
        },
        locales: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: String,
            required: true,
        },
        pluralFields: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        mappedLocales() {
            return this.locales.map((locale) => ({
                id: locale.id,
                countryCode: locale.country_iso_code.toLowerCase(),
                name: locale.name,
                isMissingContent: this.localesWithMissingContent.includes(
                    locale.id
                ),
            }))
        },

        // Retrieve the names of the locales that are missing translations
        missingTranslationLocales() {
            return this.mappedLocales.filter(
                (locale) => locale.isMissingContent
            )
        },

        /**
         * Returns the message displayed in the tooltip when hovering the warning icon
         * Will explain what locales are missing translations
         */
        missingTranslationsMessage() {
            if (this.missingTranslationLocales.length === 1) {
                // If this picker is used for changing the locale for more than one field, we change the message
                if (this.pluralFields) {
                    return this.$lang.get(
                        'Missing one or more translations for :locale',
                        {
                            locale: this.missingTranslationLocales[0].name.toLowerCase(),
                        }
                    )
                } else {
                    return this.$lang.get('Missing translation for :locale', {
                        locale: this.missingTranslationLocales[0].name.toLowerCase(),
                    })
                }
            } else if (this.missingTranslationLocales.length > 1) {
                let locales = this.missingTranslationLocales.map((locale) =>
                    locale.name.toLowerCase()
                )
                return this.$lang.get(
                    'There are translations missing for :locales',
                    {
                        locales:
                            locales.slice(0, -1).join(', ') +
                            ` ${this.$lang.get('and')} ` +
                            locales.slice(-1)[0],
                    }
                )
            }

            return ''
        },
    },
}
</script>
